import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { FileAttachment, FileAttachmentRowData } from '../../notes/shared/file-attachments/file-attachment.model';
import { AssetViewFile, SourceData } from './image-viewer.model';
import { NoteService } from "../../notes/shared/note.service";
import { NoteProjections } from "app/shared/model/request-parameter.model";
import { Note } from "../../notes/shared/note.model";
import { TemplateSegmentTypeId } from "ng/src/app/templates/shared/template.model";
import { Utils } from "../utils";

@Injectable({
	providedIn: 'root'
})
export class ImageViewerService {
	private onShowImageViewer = new Subject<SourceData | AssetViewFile>();
	onShowImageViewer$ = this.onShowImageViewer.asObservable();

	constructor(
		private noteService: NoteService,
		private ngZone: NgZone
	) {
		const self = this;

		(window as any).viewImage = (fileAttachmentId: number | string, noteId: number, event?: MouseEvent) => {
			if (event?.metaKey || event?.ctrlKey) {
				return;
			}
			event?.preventDefault();
			self.ngZone.run(
				() => {
					if (typeof fileAttachmentId === 'string') {
						if (!fileAttachmentId.startsWith('/assets')) return;
						self.onShowImageViewer.next({ href: fileAttachmentId });
						return;
					}
					self.showImageFromId(fileAttachmentId, noteId);
				}
			);
		}
	}

	/**
	 * Notifies the ImageViewerComponent to display an image galleria with new images or to open on a particular image from data it already has.
	 * The galleria will be created with its images in the same order of the fileAttachments array passed in here.
	 * @param activeImageHREF - HREF of the starting image to open in the image galleria.
	 * @param fileAttachments - Data from which the image galleria will be constructed. Pass null to reuse current file attachment data.
	 */
	showImageViewer(activeImageHREF: string, fileAttachments: FileAttachmentRowData[] | FileAttachment[] | null) {
		if (fileAttachments && '_links' in fileAttachments[0])
			fileAttachments = FileAttachmentRowData.fromFileAttachments(fileAttachments as FileAttachment[]);

		this.onShowImageViewer.next({
			fileAttachmentData: fileAttachments as FileAttachmentRowData[],
			startingImageHREF: activeImageHREF
		});
	}

	showImageFromId(fileAttachmentId: number, noteId: number) {
		this.noteService.getLogNotes({ id: noteId, projection: NoteProjections.fastTemplateValues })
			.subscribe(notes => {
				const note = (notes as unknown as Note.Projections.FastTemplateValues[])[0];
				if (!note)
					return;

				const signatureAttachments: { [id: number]: FileAttachment } = {};
				const attachmentMap: { [id:number]: FileAttachment } = {};
				const addAttachment = (a: FileAttachment) => {
					// If the attachment is known to be a possible signature, do not add to the attachmentMap.
					if (signatureAttachments[a.id])
						return;

					attachmentMap[a.id] = a;
				}

				for (const ntv of note.templateValues) {
					// Block signature type segments from having their attachments added.
					if (ntv.templateSegmentType.id === TemplateSegmentTypeId.SIGNATURE) {
						for (const attachment of (ntv?.fileAttachments ?? [])) {
							signatureAttachments[attachment.id] = attachment;

							// If the attachment matches the one requested to be displayed, display all attachments from the
							// containing notetemplatevalue and only those.
							if (attachment.id === fileAttachmentId) {
								this.showFromFileAttachments(ntv.fileAttachments!, fileAttachmentId);
								return;
							}
						}
					}
					else {
						ntv.fileAttachments?.forEach(addAttachment);
					}
					
					ntv.commentAttachments?.forEach(addAttachment);
				}
				note.attachments.forEach(addAttachment);

				this.showFromFileAttachments(Object.values(attachmentMap), fileAttachmentId);
			})
	}

	private showFromFileAttachments(attachments: FileAttachment[], startWithId?: number) {
		let idx = 0;
		const rowData = attachments
			.sort(Utils.createSortFunction(a => new Date(a.created)))
			.map((a, i) => {
				if (typeof startWithId === 'number' && a.id === startWithId) {
					idx = i;
				}
				return FileAttachmentRowData.fromFileAttachment(a);
			});
		this.showImageViewer(rowData[idx].href, rowData);
	}
}